import { HttpClient, HttpParams, HttpEvent, HttpEventType, HttpErrorResponse, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { SurnameList, PersonSearchResult, PersonLookupResult, RelationPathResult, WebFamPerson, WebFamTreeView,
         WatchedPerson, WatchedPersonAncestors, WatchedPersonRelation } from '../models/person.model';
import { Injectable } from '@angular/core';
import { WebFamEvent } from '../models/event.model';
import { FanChartData } from '../models/fanchart.model';
import { environment } from 'src/environments/environment';
import { AdvancedSearchQuery, GlobalSearchResult } from '../models/search.model';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { firstGivenName, firstGivenNameKey } from 'src/app/profile-edit/quick-add-person/name-parser';
import { I18nService } from '../i18n/i18n.service';

/** Odpowiedź /person/visible-fallback (migracja 095). */
export interface VisibleFallback {
    status: 'visible' | 'missing' | 'hidden';
    textid?: string;
    id?: string;
    relation?: 'ancestor';
    level?: number;
}

/** Osoba do pokazania zamiast ukrytej + klucz komunikatu, dlaczego. */
export interface VisibleReplacement {
    textid: string;
    /** Numeryczne id - wachlarz i profil adresują osoby po id; brak = trzeba dociągnąć. */
    id?: string;
    notice: string;
}

/** Odpowiedz o plci imienia: M/W, U = nie do rozstrzygniecia, '' = nie wiadomo. */
export type NameSex = 'M' | 'W' | 'U' | '';

class DateConfigClass {
    language: string | undefined;
    months: string[] | undefined;
}

const dateConfigSetup: DateConfigClass[] = [
    {
        language: 'generic',
        months: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
    },
    {
        language: 'pl',
        months: [
            'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
            'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'
        ],
    },
    {
        language: 'pl_short',
        months: ['sty', 'lut', 'mar', 'kwi', 'maj', 'czer', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
    },
    // Nazwy miesięcy dla interfejsu angielskiego - bez nich data wychodziła
    // bez miesiąca ("Born: 15 1912"), bo 'generic' to skróty do parsowania.
    {
        language: 'en',
        months: [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ],
    },
    {
        language: 'en_short',
        months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    }];

@Injectable()
export class PersonService {

    cThis = this;

    constructor(private httpClient: HttpClient, private i18n: I18nService) { }

    getPerson(personId: string) {
        return this.httpClient.get<WebFamPerson>(environment.backendUrl + '/person/get/tree/' + environment.currentTree
            + '/person/' + personId);
    }

    /**
     * Co pokazać zamiast profilu ukrytego prywatnością (migracja 095): dla
     * osoby żyjącej oglądanej przez gościa - najbliższy zmarły przodek.
     * `ref` to textid (drzewo) albo numeryczne id (wachlarz, profil).
     */
    getVisibleFallback(ref: string) {
        return this.httpClient.get<VisibleFallback>(
            environment.backendUrl + '/person/visible-fallback/tree/' + environment.currentTree
                + '/person/' + encodeURIComponent(ref));
    }

    /**
     * Zastępca do nawigacji, gdy widok nie znalazł osoby centralnej: przodek
     * z backendu, a bez niego osoba startowa drzewa (/tree/home - dla gościa
     * już przefiltrowana do zmarłych). Zwraca null, gdy nie ma kogo pokazać
     * albo osoba naprawdę nie istnieje - wtedy zostaje karta "nie znaleziono".
     */
    resolveVisible(ref: string): Observable<VisibleReplacement | null> {
        return this.getVisibleFallback(ref).pipe(
            catchError(() => of({ status: 'hidden' } as VisibleFallback)),
            switchMap((fallback) => {
                if (fallback.status !== 'hidden') {
                    return of(null);
                }
                if (fallback.textid && fallback.id) {
                    return of({ textid: fallback.textid, id: fallback.id, notice: 'svgtree.hiddenAncestor' });
                }
                return this.httpClient.get<{ textid: string | null; id?: string | null }>(
                    environment.backendUrl + '/tree/home/tree/' + environment.currentTree).pipe(
                    map((home) => home?.textid
                        ? { textid: home.textid, id: home.id ?? undefined, notice: 'svgtree.hiddenFallback' } : null),
                    catchError(() => of(null)));
            }));
    }

    getTreeView(personId: string) {
        return this.httpClient.get<WebFamTreeView[]>(environment.backendUrl + '/person/treeview/tree/' + environment.currentTree
            + '/person/' + personId);
    }

    /**
     * Otoczenia kilku osób jednym żądaniem (migracja 096). Baza składa je bez
     * duplikatów i pomija osoby z knownIds - te widok już ma. Pusta lista osób
     * nie robi żądania.
     */
    getTreeViewMany(textids: string[], knownIds: string[]): Observable<WebFamTreeView[]> {
        if (!textids.length) {
            return of([]);
        }
        return this.httpClient.post<WebFamTreeView[]>(
            environment.backendUrl + '/person/treeview/tree/' + environment.currentTree,
            { data: { persons: textids, known: knownIds } });
    }

    findRelation(fromId: string, toId: string, mode: 'blood' | 'all', maxDegree?: number) {
        return this.httpClient.get<RelationPathResult>(environment.backendUrl + '/person/relation/tree/'
            + environment.currentTree + '/from/' + fromId + '/to/' + toId + '/mode/' + mode
            + (maxDegree ? '/maxdeg/' + maxDegree : ''));
    }

    getPersonFull(personId: string) {
        return this.httpClient.get<WebFamPerson>(
            environment.backendUrl + '/person/get/tree/' + environment.currentTree + '/person/' + personId, {});
    }

    /** Dane do wachlarza przodków - przodkowie z numeracją ahnentafel + związki. */
    getFanChart(personId: string, generations: number) {
        return this.httpClient.get<FanChartData>(
            environment.backendUrl + '/person/fanchart/tree/' + environment.currentTree
                + '/person/' + personId + '/generations/' + generations);
    }

    uploadImage(fileData: FormData, personId: string) {
        return this.httpClient.post<unknown>(environment.backendUrl + '/person/image/upload/tree/' + environment.currentTree
            + '/person/' + personId,
            fileData, { reportProgress: true, observe: 'events' }).pipe(map(event => this.getEventMessage(event, fileData)),
                catchError(this.handleError)
            );
    }

    /** Zdjęcie do galerii osoby - nie podmienia zdjęcia profilowego. */
    uploadGalleryImage(fileData: FormData, personId: string) {
        return this.httpClient.post<unknown>(environment.backendUrl + '/person/image/upload/tree/' + environment.currentTree
            + '/person/' + personId + '/gallery',
            fileData, { reportProgress: true, observe: 'events' }).pipe(map(event => this.getEventMessage(event, fileData)),
                catchError(this.handleError)
            );
    }

    /** Wskazanie, które ze zdjęć osoby jest zdjęciem profilowym. */
    setDefaultImage(personId: string, fileId: string) {
        return this.httpClient.patch<unknown>(
            environment.backendUrl + '/person/image/default/tree/' + environment.currentTree
                + '/person/' + personId + '/file/' + fileId,
            {}
        );
    }

    removeImage(personId: string, fileId: string) {
        return this.httpClient.delete<unknown>(
            environment.backendUrl + '/person/image/remove/tree/' + environment.currentTree + '/person/' + personId + '/file/' + fileId,
            {}
        );
    }

    getSurnameList(surname: string) {
        let params = new HttpParams();
        params = params.append('skip', '0');
        params = params.append('limit', '5');
        return this.httpClient.get<SurnameList[]>(
            environment.backendUrl + '/person/list/top/tree/' + environment.currentTree + '/surname/' + surname,
            { params }
        );
    }

    personFindFullName(fullname: string) {
        const data = { fullname };
        return this.httpClient.post<PersonSearchResult>(
            environment.backendUrl + '/person/find/fullname/tree/' + environment.currentTree, { data }
        );
    }

    personFindSurname(surname: string) {
        const data = { surname };
        return this.httpClient.post<PersonSearchResult>(
            environment.backendUrl + '/person/find/surname/tree/' + environment.currentTree, { data }
        );
    }

    /**
     * Wyszukiwanie zaawansowane z poziomu drzewa: kilka kryteriów naraz,
     * opcjonalnie także w innych drzewach widocznych dla pytającego.
     */
    searchAdvanced(treeId: string, data: AdvancedSearchQuery) {
        return this.httpClient.post<GlobalSearchResult>(
            environment.backendUrl + '/person/search/advanced/tree/' + treeId, { data }
        );
    }

    /**
     * Potomkowie protoplasty noszacy nazwisko rodu - drzewo rodu jednej galezi.
     * Identyfikator galezi jest opcjonalny: gdy jest, backend dokłada do filtru
     * dodatkowe nazwiska tej galezi (linie, w ktorych nazwisko sie zmienilo).
     */
    personFindDescendants(personId: string, surname: string, branch?: string) {
        const data = { person: personId, surname, branch };
        return this.httpClient.post<PersonSearchResult>(
            environment.backendUrl + '/person/find/descendants/tree/' + environment.currentTree, { data }
        );
    }

    personFindByPlace(placeId: string) {
        return this.httpClient.get<PersonSearchResult>(
            environment.backendUrl + '/person/find/ps/tree/' + environment.currentTree + '/place/' + placeId
        );
    }

    personFindByPlaceSurname(placeId: string, surname: string) {
        const data = { surname };
        return this.httpClient.post<PersonSearchResult>(
            environment.backendUrl + '/person/find/ps/tree/' + environment.currentTree + '/place/' + placeId, { data }
        );
    }

    lookupPerson(value: string) {
        return this.httpClient.get<PersonLookupResult[]>(
            environment.backendUrl + '/person/lookup/tree/' + environment.currentTree + '/text/' + value
        );
    }

    /* --- Plec imienia z AI (migracje 094, 097 - pamiec osobna dla drzewa) ----- */

    /** Odpowiedzi w tej sesji - jedno zapytanie na (drzewo, jezyk, imie), wspolne dla wszystkich okien. */
    private nameSexCache = new Map<string, Observable<NameSex>>();
    /** Rozstrzygniete odpowiedzi - do odczytu synchronicznego w szablonach. */
    private nameSexKnown = new Map<string, 'M' | 'W'>();

    /**
     * Plec pierwszego imienia: najpierw z pamieci backendu, potem z Gemini.
     * Pusty wynik ('') znaczy "nie wiadomo" i NIE jest zapamietywany, zeby
     * chwilowy blad nie blokowal ponownej proby po chwili.
     */
    guessNameSex(names: string, lang: string): Observable<NameSex> {
        const imie = firstGivenName(names);
        if (!imie) {
            return of('' as NameSex);
        }
        // pamiec odpowiedzi jest osobna dla kazdego drzewa (migracja 097)
        const klucz = environment.currentTree + '|' + lang + '|' + firstGivenNameKey(names);
        let zapytanie = this.nameSexCache.get(klucz);
        if (!zapytanie) {
            zapytanie = this.httpClient.get<{ sex?: string }>(
                environment.backendUrl + '/person/name-sex/tree/' + environment.currentTree
                    + '/lang/' + encodeURIComponent(lang)
                    + '/name/' + encodeURIComponent(imie)
            ).pipe(
                map((odpowiedz) => (['M', 'W', 'U'].includes(odpowiedz?.sex ?? '') ? odpowiedz.sex : '') as NameSex),
                catchError(() => of('' as NameSex)),
                tap((sex) => {
                    if (sex === 'M' || sex === 'W') {
                        this.nameSexKnown.set(klucz, sex);
                    } else if (sex === '') {
                        this.nameSexCache.delete(klucz);
                    }
                }),
                shareReplay(1)
            );
            this.nameSexCache.set(klucz, zapytanie);
        }
        return zapytanie;
    }

    /** Plec z juz otrzymanej odpowiedzi albo undefined - bez zadnego zapytania. */
    peekNameSex(names: string, lang: string): 'M' | 'W' | undefined {
        const imie = firstGivenName(names);
        return imie
            ? this.nameSexKnown.get(environment.currentTree + '|' + lang + '|' + firstGivenNameKey(names))
            : undefined;
    }

    savePerson(personData: WebFamPerson) {
        return this.httpClient.post<WebFamPerson>(
            environment.backendUrl + '/person/update/tree/' + environment.currentTree,
            { data: personData }
        );
    }

    removePerson(id: string) {
        return this.httpClient.delete<string>(environment.backendUrl + '/person/remove/tree/' + environment.currentTree + '/person/' + id);
    }

    getAncestors(personId: string) {
        return this.httpClient.get<{[key: string]: number}>(
            environment.backendUrl + '/person/ancestors/tree/' + environment.currentTree + '/person/' + personId
        );
    }

    /** Obserwowane osoby zalogowanego użytkownika - prywatna lista, jedna na drzewo. */
    getWatchedPersons() {
        return this.httpClient.get<WatchedPerson[]>(
            environment.backendUrl + '/person/watch/tree/' + environment.currentTree
        );
    }

    /** To samo co wyżej, ale z przodkami każdej obserwowanej osoby - do badge'ów na listach. */
    getWatchedAncestors() {
        return this.httpClient.get<WatchedPersonAncestors[]>(
            environment.backendUrl + '/person/watch/ancestors/tree/' + environment.currentTree
        );
    }

    /** Czyim przodkiem - spośród obserwowanych - jest ta osoba. Sam poziom, bez zbiorów przodków. */
    getWatchedRelation(personId: string) {
        return this.httpClient.get<WatchedPersonRelation[]>(
            environment.backendUrl + '/person/watch/relation/tree/' + environment.currentTree + '/person/' + personId
        );
    }

    addWatchedPerson(personId: string) {
        return this.httpClient.put<{ watched: boolean }>(
            environment.backendUrl + '/person/watch/tree/' + environment.currentTree + '/person/' + personId, {}
        );
    }

    removeWatchedPerson(personId: string) {
        return this.httpClient.delete<{ watched: boolean }>(
            environment.backendUrl + '/person/watch/tree/' + environment.currentTree + '/person/' + personId
        );
    }

    getRelationshipText(level: number, sex?: string): string {
    // Nazwy pokrewieństw idą przez słownik - w drzewie angielskim „prababcia”
    // wyglądałaby dziwnie obok reszty interfejsu.
    if (sex === 'W' || sex === 'F') {
      switch (level) {
        case 1: return this.i18n.t('rel.mother');
        case 2: return this.i18n.t('rel.grandmother');
        case 3: return this.i18n.t('rel.greatGrandmother');
        case 4: return this.i18n.t('rel.greatGreatGrandmother');
        default: return level > 4 ? this.i18n.t('rel.nGreatGrandmother', { n: level - 2 }) : '';
      }
    }
    switch (level) {
      case 1: return sex === 'M' ? this.i18n.t('rel.father') : this.i18n.t('rel.parent');
      case 2: return this.i18n.t('rel.grandfather');
      case 3: return this.i18n.t('rel.greatGrandfather');
      case 4: return this.i18n.t('rel.greatGreatGrandfather');
      default: return level > 4 ? this.i18n.t('rel.nGreatGrandfather', { n: level - 2 }) : '';
    }
  }

  formatDisplayEvents(events: WebFamEvent[]) {
        let inData = '';
        events?.forEach((event) => {
            switch (event.event_type) {
                case 'birth': {
                    if (event.date) {
                        inData += this.i18n.t('date.bornShort') + ' ' + this.formatDate(event.date);
                    }
                    break;
                }
                case 'death': {
                    if (event.date) {
                        inData += ' ' + this.i18n.t('date.diedShort') + ' ' + this.formatDate(event.date);
                    }
                    break;
                }
            }
        });
        return inData;
    }

    /*formatLookupPerson(value?, full?:boolean): String {
        let inData: string = '';
        if (typeof value == "string") return value;
        if (value) {
            if (full)
                inData = this.formatDisplayEvents(value.events);
            if (inData != '')
                inData = value.searchString + ', ' + inData
            else inData = value.searchString;
            return inData;
        }
    }*/

    /*    formatDisplayNames(names: WebFamPersonSurname[]) {
            if (names) {
                return names.find(res => res.isdefault === true)?.name;
            } else {return '';}
        }
    */
    checkSurname(person: WebFamPerson, language: string, surnameType: string) {
        if ((person) && (person.names)) {
            return person.names.find(res => (res.language === language && res.surname_type === surnameType)) ? true : false;
        } else { return false; }
    }

    /**
     * Wariant nazwiska do wyświetlenia. Kolejność: żądany język, potem język
     * domyślny profilu, na końcu jakikolwiek zapisany wariant.
     *
     * Ostatni krok jest tu najważniejszy: profil ma jeden język domyślny
     * (`person.language`) i osobną listę wariantów, a te dwie rzeczy potrafią
     * się rozjechać - wystarczy dodać nazwisko po francusku i usunąć polską
     * zakładkę. Bez tego zapasu osoba pokazywała się wszędzie jako "N. N.",
     * mimo że nazwisko siedziało w bazie.
     */
    private pickName(person: WebFamPerson, language: string, surnameType: string) {
        const names = person.names ?? [];
        const wgJezyka = (lang: string | undefined) => (lang
            ? names.find(res => res.language === lang && res.surname_type === surnameType)
            ?? names.find(res => res.language === lang && res.surname_type === 'knownas')
            : undefined);
        return wgJezyka(language)
            ?? wgJezyka(person.language)
            ?? names.find(res => res.surname_type === surnameType)
            ?? names.find(res => res.surname_type === 'knownas');
    }

    formatDisplayName(person: WebFamPerson, language: string, surnameType: string) {
        let output = '';
        if ((person) && (person.names)) {
            const retSurname = this.pickName(person, language, surnameType);
            if (retSurname) {
                output = [retSurname.name_prefix, retSurname.name ? retSurname.name : 'N.'].join(' ');
            }
            return output;
        } else { return output; }
    }

    formatDisplaySurname(person: WebFamPerson, language: string, surnameType: string) {
        let output = '';
        if ((person) && (person.names)) {
            const retSurname = this.pickName(person, language, surnameType);
            if (retSurname) {
                output = [retSurname.surname_prefix, retSurname.surname ? retSurname.surname : 'N.', retSurname.surname_suffix].join(' ');
            }
            return output;
        } else { return output; }
    }


    scanMonth(text: string): number {
        for (const monthLang of dateConfigSetup) {
            const monthPos = monthLang.months?.findIndex(res => res === text);
            if ((monthPos !== undefined) && (monthPos !== -1)) {
                return monthPos;
            }
        }
        return NaN;
    }

    getMonth(monthNumber: number, language: string, short: boolean) {

        if (short) {
            language = language + '_short';
        }
        const found = dateConfigSetup.find(res => res.language === language)?.months;
        return found ? found[monthNumber] : '';
    }

    getDateStandardInt(parts: string[]): string {
        let day = -1;
        let month = -1;
        let year = -1;

        // find year
        for (const [index, part] of parts.entries()) {
            const parsed = parseInt(part, 10);
            if ((parsed) && (parsed > 31)) {
                year = parsed;
                parts[index] = '';
            }
        }
        if (year === -1) {
            return '';
        }

        // find month string
        for (const [index, part] of parts.entries()) {
            if (part !== '') {
                const parsed = parseInt(part, 10);
                if (!parsed) {
                    month = this.scanMonth(part);
                    parts[index] = '';
                }
            }
        }
        // find day
        for (const [index, part] of parts.entries()) {
            if (part !== '') {
                const parsed = parseInt(part, 10);
                if (!parsed) {
                    // cannot parse date
                    return '';
                }
                if ((month !== -1) && (parsed <= 31)) {
                    // here we know already it will be day
                    day = parsed;
                    parts[index] = '';
                    break;
                } else {
                    if ((parsed > 12) && (parsed < 31)) {
                        day = parsed;
                        parts[index] = '';
                        break;
                    }
                    if (parts.length === 3) {
                        return '';
                    }
                }
            }
        }
        // find month (if not found yet)
        if (month === -1) {
            for (const [index, part] of parts.entries()) {
                if (part !== '') {
                    const parsed = parseInt(part, 10);
                    if (!parsed) {
                        // cannot parse date
                        return '';
                    }
                    if ((parsed >= 1) && (parsed <= 12)) {
                        // found month
                        month = parsed;
                        parts[index] = '';
                        break;
                    } else {
                        return '';
                    }
                }
            }
        }

        if ((day > 0) && (month !== -1) && (year > 0)) {
            return year.toString()
                + ((month < 10) ? '0' + month?.toString() : month?.toString())
                + ((day < 10) ? '0' + day.toString() : day.toString());
        }
        if ((month !== -1) && (year > 0)) {
            return year.toString() + month?.toString() + '01';
        }
        if (year > 0) {
            return year.toString() + '0101';
        }
        return '';
    }

    getDateStandard(date: string) {
        // prepare date
        let output = '';
        if (!date) {
            return '';
        }
        let intDate: string = date.trim();
        intDate = intDate.replace(/[-\s\/]/g, '.');
        intDate = intDate.toLowerCase();
        const intDateArr = intDate.split('.');

        if (intDateArr.length > 0) {
            if (intDateArr[0] === 'abt') {
                intDateArr.shift();
                output = this.getDateStandardInt(intDateArr);
            } else
                if (intDateArr[0] === 'bef') {
                    intDateArr.shift();
                    output = this.getDateStandardInt(intDateArr);
                } else
                    if (intDateArr[0] === 'aft') {
                        intDateArr.shift();
                        output = this.getDateStandardInt(intDateArr);
                    } else
                        if (intDateArr[0] === 'bet') {
                            intDateArr.shift();
                            const intDateArr2 = intDate.split('and');
                            if (intDateArr2.length === 2) {
                                const part1 = intDateArr2[0].split('.');
                                part1.shift();
                                output = this.getDateStandardInt(intDateArr);
                            } else {
                                return date;
                            }
                        } else {
                            output = this.getDateStandardInt(intDateArr);
                        }
        }
        if (output) {
            return output;
        } else {
            return '';
        }
    }

    formatDateInt(parts: string[], short: boolean): string {

        let day = -1;
        let month: number | undefined = -1;
        let year = -1;

        // find year
        for (const [index, part] of parts.entries()) {
            const parsed = parseInt(part, 10);
            if ((parsed) && (parsed > 31)) {
                year = parsed;
                parts[index] = '';
            }
        }
        if (year === -1) {
            return '';
        }

        // find month string
        for (const [index, part] of parts.entries()) {
            if (part !== '') {
                const parsed = parseInt(part, 10);
                if (!parsed) {
                    month = this.scanMonth(part);
                    parts[index] = '';
                }
            }
        }
        // find day
        for (const [index, part] of parts.entries()) {
            if (part !== '') {
                const parsed = parseInt(part, 10);
                if (!parsed) {
                    // cannot parse date
                    return '';
                }
                if ((month !== -1) && (parsed <= 31)) {
                    // here we know already it will be day
                    day = parsed;
                    parts[index] = '';
                    break;
                } else {
                    if ((parsed > 12) && (parsed < 31)) {
                        day = parsed;
                        parts[index] = '';
                        break;
                    }
                    if (parts.length === 3) {
                        return '';
                    }
                }
            }
        }
        // find month (if not found yet)
        if (month === -1) {
            for (const [index, part] of parts.entries()) {
                if (part !== '') {
                    const parsed = parseInt(part, 10);
                    if (!parsed) {
                        // cannot parse date
                        return '';
                    }
                    if ((parsed >= 1) && (parsed <= 12)) {
                        // found month
                        month = parsed - 1;
                        parts[index] = '';
                        break;
                    } else {
                        return '';
                    }
                }
            }
        }

        if ((day > 0) && (month !== -1) && (year > 0)) {
            return day.toString() + ' ' + this.getMonth(month, this.i18n.lang, short) + ' ' + year.toString();
        }
        if ((month !== -1) && (year > 0)) {
            return this.getMonth(month, this.i18n.lang, short) + ' ' + year.toString();
        }
        if (year > 0) {
            return year.toString();
        }
        return '';
    }

    formatDate(date: string) {
        // prepare date
        let output = '';
        let intDate: string = date.trim();
        intDate = intDate.replace(/[-\s\/]/g, '.');
        intDate = intDate.toLowerCase();
        const intDateArr = intDate.split('.');
        //        console.log(intDateArr);

        if (intDateArr.length > 0) {
            if (intDateArr[0] === 'abt') {
                intDateArr.shift();
                output = [this.i18n.t('date.about'),
                    this.formatDateInt(intDateArr, false)].join(' ');
            } else
                if (intDateArr[0] === 'bef') {
                    intDateArr.shift();
                    output = [this.i18n.t('date.before'),
                        this.formatDateInt(intDateArr, false)].join(' ');
                } else
                    if (intDateArr[0] === 'aft') {
                        intDateArr.shift();
                        output = [this.i18n.t('date.after'),
                            this.formatDateInt(intDateArr, false)].join(' ');
                    } else
                        if (intDateArr[0] === 'bet') {
                            intDateArr.shift();
                            const intDateArr2 = intDate.split('and');
                            if (intDateArr2.length === 2) {
                                const part1 = intDateArr2[0].split('.');
                                part1.shift();
                                output = [this.i18n.t('date.between'),
                                    this.formatDateInt(part1, false),
                                    this.i18n.t('date.and'),
                                    this.formatDateInt(intDateArr2[1].trim().split('.'), false)].join(' ');
                            } else {
                                return date;
                            }

                        } else {
                            output = this.formatDateInt(intDateArr, false);
                        }
        }
        if (output) {
            return output;
        } else {
            return date;
        }
    }

    /**
     * Data na karcie drzewa: krótkie miesiące i słowne kwalifikatory,
     * np. 'ok. 12 maj 1850', 'przed 1900', 'między 1850 a 1860'.
     * Krótsza niż formatDate (pełne miesiące), a bez symboli ~ < > z formatDateShort.
     */
    formatTreeDate(date: string): string {
        let output = '';
        let intDate: string = date.trim();
        intDate = intDate.replace(/[-\s\/]/g, '.');
        intDate = intDate.toLowerCase();
        const intDateArr = intDate.split('.');

        if (intDateArr.length > 0) {
            if (intDateArr[0] === 'abt') {
                intDateArr.shift();
                output = [this.i18n.t('date.aboutShort'), this.formatDateInt(intDateArr, true)].join(' ');
            } else if (intDateArr[0] === 'bef') {
                intDateArr.shift();
                output = [this.i18n.t('date.before'), this.formatDateInt(intDateArr, true)].join(' ');
            } else if (intDateArr[0] === 'aft') {
                intDateArr.shift();
                output = [this.i18n.t('date.after'), this.formatDateInt(intDateArr, true)].join(' ');
            } else if (intDateArr[0] === 'bet') {
                intDateArr.shift();
                const intDateArr2 = intDate.split('and');
                if (intDateArr2.length === 2) {
                    const part1 = intDateArr2[0].split('.');
                    part1.shift();
                    output = [this.i18n.t('date.betweenShort'),
                        this.formatDateInt(part1, true),
                        'a',
                        this.formatDateInt(intDateArr2[1].trim().split('.'), true)].join(' ');
                } else {
                    return date;
                }
            } else {
                output = this.formatDateInt(intDateArr, true);
            }
        }
        return output ? output : date;
    }

    formatDateShort(date: string) {
        // prepare date
        let output = '';
        let intDate: string = date.trim();
        intDate = intDate.replace(/[-\s\/]/g, '.');
        intDate = intDate.toLowerCase();
        const intDateArr = intDate.split('.');

        if (intDateArr.length > 0) {
            if (intDateArr[0] === 'abt') {
                intDateArr.shift();
                output = ['~',
                    this.formatDateInt(intDateArr, true)].join(' ');
            } else
                if (intDateArr[0] === 'bef') {
                    intDateArr.shift();
                    output = ['< ',
                        this.formatDateInt(intDateArr, true)].join(' ');
                } else
                    if (intDateArr[0] === 'aft') {
                        intDateArr.shift();
                        output = ['> ',
                            this.formatDateInt(intDateArr, true)].join(' ');
                    } else
                        if (intDateArr[0] === 'bet') {
                            intDateArr.shift();
                            const intDateArr2 = intDate.split('and');
                            if (intDateArr2.length === 2) {
                                const part1 = intDateArr2[0].split('.');
                                part1.shift();
                                output = [this.formatDateInt(part1, true),
                                    ' - ',
                                this.formatDateInt(intDateArr2[1].trim().split('.'), true)].join(' ');
                            } else {
                                return date;
                            }

                        } else {
                            output = this.formatDateInt(intDateArr, true);
                        }
        }
        if (output) {
            return output;
        } else {
            return date;
        }
    }

    public formatDisplayFullName(person: WebFamPerson | undefined, displayDates: boolean = false) {
        let output = '';
        if (typeof person === 'string') {
            return person;
        }
        if (person) {
            if (person.names) {
                // ten sam zapas co w pickName - profil z nazwiskiem tylko
                // w innym języku nie może się pokazywać jako "N. N."
                let retSurname = person.names.find(res => (res.language === person.language && res.surname_type === 'birth'));
                if (!retSurname) {
                    retSurname = person.names.find(res => res.language === person.language)
                        ?? person.names.find(res => res.surname_type === 'birth')
                        ?? person.names.find(res => res.name || res.surname);
                }
                let outputSurname: string;

                if (retSurname) {
                    if (retSurname.name) {
                        output = retSurname.name_prefix ? retSurname.name_prefix + ' ' + retSurname.name + ' ' : '' + retSurname.name;
                    } else {
                        output += 'N.';
                    }
                    outputSurname = [retSurname.surname_prefix, retSurname.surname ? retSurname.surname : 'N.',
                    retSurname.surname_suffix].join(' ');
                    outputSurname = outputSurname.trim();
                } else {
                    outputSurname = 'N.';
                }

                output += ' ' + outputSurname;

            } else {
                output += 'N. N.';
            }

            if (displayDates && person.events) {
                output += ' ' + this.formatDisplayEvents(person.events);
            }

            return output;
        }
        return output;
    }

    /**
     * Funkcja dla [displayWith] podpowiedzi. Część list trzyma w opcji cały
     * wynik wyszukiwania ({ person: ... }), część samą osobę - bez rozpakowania
     * te pierwsze pokazywały po wyborze "N. N." zamiast nazwiska.
     */
    formatDisplayFullNameDatesWrapper() {
        return (value: WebFamPerson | PersonLookupResult | string | undefined) => {
            if (typeof value === 'string') {
                return value;
            }
            const person = (value as PersonLookupResult | undefined)?.person ?? (value as WebFamPerson | undefined);
            return this.formatDisplayFullName(person, true);
        };
     }

    private getEventMessage(event: HttpEvent<unknown>, formData: FormData) {
        switch (event.type) {
            case HttpEventType.UploadProgress:
                return this.fileUploadProgress(event);
            case HttpEventType.Response:
                return this.apiResponse(event);
        }
        return undefined;
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.error(`Backend returned code ${error.status}`);
        }
        // return an observable with a user-facing error message
        return throwError('Something bad happened. Please try again later.');
    }

    private fileUploadProgress(event: HttpProgressEvent) {
        const percentDone = Math.round(100 * event.loaded / (event.total ? event.total : 0));
        console.log('upload progress');
        console.log(percentDone);
        return { status: 'progress', message: percentDone };
    }

    private apiResponse(event: HttpResponse<unknown>) {
        return event.body;
    }

}
